/**
 * Eigen allowlist over de mail-HTML. De server stuurt al gestripte,
 * veilige tags; dit is de tweede grendel in het scherm. Alleen p, br,
 * b, strong, i, em, u, a (href http, https of mailto), ul, ol en li
 * blijven staan (u sinds sessie 70: het mailvak kan onderstrepen, de
 * server-kant `schone_html` liet u al door). Elke andere tag verdwijnt en zijn tekst blijft als tekst staan;
 * script, style en dergelijke gaan er met inhoud en al uit. Geen enkel
 * attribuut overleeft behalve een gecontroleerde href.
 *
 * Links openen in een nieuw tabblad (target _blank, rel noopener
 * noreferrer). Een kale URL in de tekst (buiten een <a>) wordt ook een
 * link, met dezelfde regels als platte tekst (zie links.tsx). De opmaak
 * komt uit midden.css, gelijk aan .v6-link.
 */
import { vindLinks } from "./links";

const TOEGESTAAN = new Set(["p", "br", "b", "strong", "i", "em", "u", "a", "ul", "ol", "li"]);
const WEG_MET_INHOUD = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "template",
  "noscript",
  "svg",
  "math",
  "head",
  "title",
]);

function veiligeHref(href: string | null): string | null {
  if (!href) return null;
  const schoon = href.trim();
  return /^(https?:|mailto:)/i.test(schoon) ? schoon : null;
}

function nieuwTabblad(a: Element, href: string): void {
  a.setAttribute("href", href);
  a.setAttribute("target", "_blank");
  a.setAttribute("rel", "noopener noreferrer");
}

/** Tekst als tekstnodes; buiten een link worden kale URL's een <a>. */
function kopieerTekst(tekst: string, doel: Node, doc: Document, inLink: boolean): void {
  if (inLink) {
    doel.appendChild(doc.createTextNode(tekst));
    return;
  }
  for (const deel of vindLinks(tekst)) {
    if (deel.soort === "tekst") {
      doel.appendChild(doc.createTextNode(deel.tekst));
      continue;
    }
    const a = doc.createElement("a");
    nieuwTabblad(a, deel.href);
    a.appendChild(doc.createTextNode(deel.tekst));
    doel.appendChild(a);
  }
}

function kopieer(bron: Node, doel: Node, doc: Document, inLink = false): void {
  for (const kind of Array.from(bron.childNodes)) {
    if (kind.nodeType === Node.TEXT_NODE) {
      kopieerTekst(kind.textContent ?? "", doel, doc, inLink);
      continue;
    }
    if (kind.nodeType !== Node.ELEMENT_NODE) continue;
    const el = kind as Element;
    const naam = el.tagName.toLowerCase();
    if (WEG_MET_INHOUD.has(naam)) continue;
    if (!TOEGESTAAN.has(naam)) {
      // Onbekende tag: de tekst blijft, de tag niet.
      kopieer(el, doel, doc, inLink);
      continue;
    }
    const nieuw = doc.createElement(naam);
    if (naam === "a") {
      const href = veiligeHref(el.getAttribute("href"));
      if (href) nieuwTabblad(nieuw, href);
    }
    kopieer(el, nieuw, doc, inLink || naam === "a");
    doel.appendChild(nieuw);
  }
}

export function veiligeHtml(html: string): string {
  // Een los document zonder browsercontext: daarin draait geen script en
  // laadt geen afbeelding, ook niet tijdens het ontleden.
  const doc = document.implementation.createHTMLDocument("");
  const bron = new DOMParser().parseFromString(html, "text/html");
  const houder = doc.createElement("div");
  kopieer(bron.body, houder, doc);
  return houder.innerHTML;
}
